import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Avatar,
    Tooltip,
    Alert,
    CircularProgress,
    Divider,
    FormHelperText
} from '@mui/material';
import {    Delete,
    Edit,
    Refresh,
    PersonAdd,
    Save,
    Cancel,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { ROLE_LABELS, ROLE_OPTIONS } from '../../constants/domain';
import { avatarUrl } from '../../utils/avatar';

const TeamManagement = ({ onTeamUpdate }) => {
    const { logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        full_name: '',
        role: 'engineer',
        department: 'Engineering',
        is_active: true
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setAuthError(false);
        try {
            console.log('Fetching users with token...');
            const token = localStorage.getItem('token');
            console.log('Token exists:', !!token);
            
            // Make sure token is in headers
            if (token) {
                api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await api.get('/auth/users');
            console.log('Users response:', response.data);
            
            // Filter out admin users from management list
            const filteredUsers = response.data.filter(u => u.role !== 'admin');
            setUsers(filteredUsers);
            
            if (filteredUsers.length > 0) {
                toast.success(`Loaded ${filteredUsers.length} team members`);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            console.error('Error details:', error.response?.data);
            
            if (error.response?.status === 401) {
                setAuthError(true);
                toast.error('Session expired. Please login again.');
                // Optionally redirect to login
                // logout();
                // window.location.href = '/login';
            } else if (error.response?.status === 403) {
                toast.error('You do not have permission to view users.');
            } else {
                toast.error('Failed to load users: ' + (error.response?.data?.message || error.message));
            }
            
            // Set some default users for testing
            setUsers([
                { id: 3, email: 'admin@domino.com', full_name: 'Admin User', role: 'admin', department: 'Management', is_active: true },
                { id: 4, email: 'engineer@domino.com', full_name: 'Engineer User', role: 'engineer', department: 'Engineering', is_active: true },
                { id: 5, email: 'test@domino.com', full_name: 'Test User', role: 'engineer', department: 'Engineering', is_active: true }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (user = null) => {
        if (user) {
            setSelectedUser(user);
            setFormData({
                email: user.email,
                password: '',
                full_name: user.full_name,
                role: user.role,
                department: user.department || 'Engineering',
                is_active: user.is_active !== undefined ? user.is_active : true
            });
        } else {
            setSelectedUser(null);
            setFormData({
                email: '',
                password: '',
                full_name: '',
                role: 'engineer',
                department: 'Engineering',
                is_active: true
            });
        }
        setErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setFormData({
            email: '',
            password: '',
            full_name: '',
            role: 'engineer',
            department: 'Engineering',
            is_active: true
        });
        setErrors({});
    };

    const handleOpenDeleteDialog = (user) => {
        setSelectedUser(user);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedUser(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.full_name) {
            newErrors.full_name = 'Full name is required';
        }
        if (!formData.role) {
            newErrors.role = 'Role is required';
        }
        if (!selectedUser && !formData.password) {
            newErrors.password = 'Password is required for new users';
        } else if (!selectedUser && formData.password && formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        setLoading(true);
        try {
            if (selectedUser) {
                // Update existing user
                const updateData = {
                    full_name: formData.full_name,
                    role: formData.role,
                    department: formData.department,
                    is_active: formData.is_active
                };
                if (formData.password) {
                    updateData.password = formData.password;
                }
                await api.put(`/auth/users/${selectedUser.id}`, updateData);
                toast.success(`User ${formData.full_name} updated successfully!`);
            } else {
                // Create new user
                await api.post('/auth/users', {
                    email: formData.email,
                    password: formData.password,
                    full_name: formData.full_name,
                    role: formData.role,
                    department: formData.department
                });
                toast.success(`User ${formData.full_name} created successfully!`);
            }
            fetchUsers();
            if (onTeamUpdate) onTeamUpdate();
            handleCloseDialog();
        } catch (error) {
            console.error('Error saving user:', error);
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to save user');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await api.delete(`/auth/users/${selectedUser.id}`);
            toast.success(`User ${selectedUser.full_name} deleted successfully!`);
            fetchUsers();
            if (onTeamUpdate) onTeamUpdate();
            handleCloseDeleteDialog();
        } catch (error) {
            console.error('Error deleting user:', error);
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to delete user');
            }
        } finally {
            setLoading(false);
        }
    };

    const getRoleColor = (role) => {
        const colors = {
            admin: 'error',
            head: 'primary',
            manager: 'success',
            engineer: 'info',
        };
        return colors[role] || 'default';
    };

    const getRoleLabel = (role) => ROLE_LABELS[role] || role;

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Team Management
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Manage your engineering team members
                    </Typography>
                </Box>
                <Box display="flex" gap={2}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchUsers}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={() => handleOpenDialog()}
                        disabled={authError}
                    >
                        Add Engineer
                    </Button>
                </Box>
            </Box>

            {authError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        Authentication error. Please <strong>logout and login again</strong> to continue.
                    </Typography>
                    <Button 
                        variant="outlined" 
                        color="error" 
                        size="small" 
                        sx={{ mt: 1 }}
                        onClick={() => {
                            logout();
                            window.location.href = '/login';
                        }}
                    >
                        Go to Login
                    </Button>
                </Alert>
            )}

            {/* Stats Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                            {users.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Total Engineers
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                            {users.filter(u => u.is_active !== false && u.role !== 'admin').length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Active Engineers
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                            {users.filter(u => u.role === 'head' || u.role === 'manager').length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Head/Manager
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Users Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body1" color="textSecondary">
                                        No engineers found. Click "Add Engineer" to get started.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.filter(u => u.role !== 'admin').map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar src={avatarUrl(user.avatar_path)}
                                                sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                                {user.full_name?.charAt(0) || 'U'}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight="bold">
                                                {user.full_name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getRoleLabel(user.role)}
                                            color={getRoleColor(user.role)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{user.department || 'Engineering'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.is_active !== false ? 'Active' : 'Inactive'}
                                            color={user.is_active !== false ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(user)}
                                                color="primary"
                                                disabled={authError}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDeleteDialog(user)}
                                                color="error"
                                                disabled={authError}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        {selectedUser ? <Edit /> : <PersonAdd />}
                        <Typography variant="h6">
                            {selectedUser ? 'Edit Engineer' : 'Add New Engineer'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Full Name *"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    error={!!errors.full_name}
                                    helperText={errors.full_name}
                                    disabled={loading}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email *"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    disabled={loading || !!selectedUser}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={selectedUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    disabled={loading}
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" error={!!errors.role}>
                                    <InputLabel>Role *</InputLabel>
                                    <Select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        label="Role *"
                                        disabled={loading}
                                    >
                                        {ROLE_OPTIONS.map(role => (
                                            <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
                                        ))}
                                    </Select>
                                    {errors.role && (
                                        <FormHelperText error>{errors.role}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Department</InputLabel>
                                    <Select
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        label="Department"
                                        disabled={loading}
                                    >
                                        <MenuItem value="Engineering">Engineering</MenuItem>
                                        <MenuItem value="Design">Design</MenuItem>
                                        <MenuItem value="Quality">Quality</MenuItem>
                                        <MenuItem value="Production">Production</MenuItem>
                                        <MenuItem value="Management">Management</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {selectedUser && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="is_active"
                                            value={formData.is_active}
                                            onChange={handleChange}
                                            label="Status"
                                            disabled={loading}
                                        >
                                            <MenuItem value={true}>Active</MenuItem>
                                            <MenuItem value={false}>Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={loading}
                        startIcon={<Cancel />}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    >
                        {loading ? 'Saving...' : selectedUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" color="error">
                        Delete Engineer
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Are you sure you want to delete <strong>{selectedUser?.full_name}</strong>?
                            <br />
                            This action cannot be undone. All parts assigned to this user will need to be reassigned.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDeleteDialog}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Delete />}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TeamManagement;